import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';

/**
 * Shared UI primitives for WPAA Academy article pages.
 * Consistent typography + callouts across all guides.
 */

export const H2 = ({ id, children }) => (
  <h2 id={id} className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 scroll-mt-24">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">{children}</h3>
);

export const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
);

export const Callout = ({ variant = 'info', title, children }) => {
  const styles = {
    info: 'bg-teal-50 border-teal-200 text-teal-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    danger: 'bg-rose-50 border-rose-200 text-rose-900'
  };
  const iconColors = {
    info: 'text-teal-600',
    warning: 'text-amber-600',
    success: 'text-emerald-600',
    danger: 'text-rose-600'
  };
  const Icon = variant === 'warning' || variant === 'danger' ? AlertTriangle : Info;
  return (
    <div className={`border-l-4 rounded-r-lg p-5 my-6 ${styles[variant]}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} className={`flex-shrink-0 mt-0.5 ${iconColors[variant]}`} />
        <div>
          {title && <p className="font-bold mb-1">{title}</p>}
          <div className="text-sm leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
};

export const CheckList = ({ items }) => (
  <ul className="list-none space-y-2 my-4">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-2 text-gray-700">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-600 flex-shrink-0 mt-0.5">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const CrossList = ({ items }) => (
  <ul className="list-none space-y-2 my-4">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-2 text-gray-700">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-rose-500 flex-shrink-0 mt-0.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const BulletList = ({ items }) => (
  <ul className="list-disc pl-6 my-4 text-gray-700 space-y-1.5">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
